// ID型の定義（ブランド型）
// 各概念のIDを独立した型として定義し、型レベルでの混同を防ぐ

// データセット関連
declare const DatasetIdBrand: unique symbol;
export type DatasetId = string & { [DatasetIdBrand]: never };

declare const ColumnIdBrand: unique symbol;
export type ColumnId = string & { [ColumnIdBrand]: never };

declare const ColumnNameBrand: unique symbol;
export type ColumnName = string & { [ColumnNameBrand]: never };

// レポート関連
declare const ReportIdBrand: unique symbol;
export type ReportId = string & { [ReportIdBrand]: never };

declare const ReportElementIdBrand: unique symbol;
export type ReportElementId = string & { [ReportElementIdBrand]: never };

// モデル関連
declare const ModelIdBrand: unique symbol;
export type ModelId = string & { [ModelIdBrand]: never };

// ワークスペース関連
declare const WorkspaceIdBrand: unique symbol;
export type WorkspaceId = string & { [WorkspaceIdBrand]: never };

declare const LayoutStyleIdBrand: unique symbol;
export type LayoutStyleId = string & { [LayoutStyleIdBrand]: never };

// プロジェクト関連
declare const ProjectIdBrand: unique symbol;
export type ProjectId = string & { [ProjectIdBrand]: never };

// ペイン・タブ関連
declare const PaneIdBrand: unique symbol;
export type PaneId = string & { [PaneIdBrand]: never };

declare const TabIdBrand: unique symbol;
export type TabId = string & { [TabIdBrand]: never };

// 変換操作関連
declare const TransformOperationIdBrand: unique symbol;
export type TransformOperationId = string & { [TransformOperationIdBrand]: never };

// 通知関連
declare const NotificationIdBrand: unique symbol;
export type NotificationId = string & { [NotificationIdBrand]: never };

// ID生成関数
export function createDatasetId(id: string): DatasetId {
  return id as DatasetId;
}

export function createColumnId(id: string): ColumnId {
  return id as ColumnId;
}

export function createColumnName(name: string): ColumnName {
  return name as ColumnName;
}

export function createReportId(id: string): ReportId {
  return id as ReportId;
}

export function createReportElementId(id: string): ReportElementId {
  return id as ReportElementId;
}

export function createModelId(id: string): ModelId {
  return id as ModelId;
}

export function createWorkspaceId(id: string): WorkspaceId {
  return id as WorkspaceId;
}

export function createLayoutStyleId(id: string): LayoutStyleId {
  return id as LayoutStyleId;
}

export function createProjectId(id: string): ProjectId {
  return id as ProjectId;
}

export function createPaneId(id: string): PaneId {
  return id as PaneId;
}

export function createTabId(id: string): TabId {
  return id as TabId;
}

export function createTransformOperationId(id: string): TransformOperationId {
  return id as TransformOperationId;
}

export function createNotificationId(id: string): NotificationId {
  return id as NotificationId;
}

// 型ガード関数
export function isDatasetId(value: unknown): value is DatasetId {
  return typeof value === 'string';
}

export function isColumnId(value: unknown): value is ColumnId {
  return typeof value === 'string';
}

export function isColumnName(value: unknown): value is ColumnName {
  return typeof value === 'string';
}

export function isReportId(value: unknown): value is ReportId {
  return typeof value === 'string';
}

export function isModelId(value: unknown): value is ModelId {
  return typeof value === 'string';
}

export function isWorkspaceId(value: unknown): value is WorkspaceId {
  return typeof value === 'string';
}

export function isProjectId(value: unknown): value is ProjectId {
  return typeof value === 'string';
}

// 配列変換ヘルパー
export function asDatasetIds(ids: string[]): DatasetId[] {
  return ids.map(createDatasetId);
}

export function asColumnIds(ids: string[]): ColumnId[] {
  return ids.map(createColumnId);
}

export function asColumnNames(names: string[]): ColumnName[] {
  return names.map(createColumnName);
}

// Record型アクセスヘルパー
export function getFromDatasetRecord<T>(
  record: Record<DatasetId, T>,
  id: DatasetId
): T | undefined {
  return record[id];
}

export function getFromColumnRecord<T>(
  record: Record<ColumnId, T>,
  id: ColumnId
): T | undefined {
  return record[id];
}

export function getFromProjectRecord<T>(
  record: Record<ProjectId, T>,
  id: ProjectId
): T | undefined {
  return record[id];
}

// Record型のキー変換ヘルパー
export function recordKeysAsDatasetIds<T>(record: Record<string, T>): Record<DatasetId, T> {
  return record as Record<DatasetId, T>;
}

export function recordKeysAsColumnIds<T>(record: Record<string, T>): Record<ColumnId, T> {
  return record as Record<ColumnId, T>;
}